// Installing the app (ADR 0005). Chromium browsers fire `beforeinstallprompt`
// once, early in the page's life, when the app can be installed: we keep
// the event so our own button can open the browser's install dialog later.
// iOS has no such event, only Share → Add to Home Screen, so there we can
// only say how. Firefox on a desktop can't install at all.

// Chromium's event; not in TypeScript's DOM types.
export interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/**
 * - `installed`: running as the installed app.
 * - `prompt`: the browser will install it at our button.
 * - `ios`: installable by hand, from Safari's Share menu.
 * - `none`: this browser can't, or the app is already installed here.
 */
export type InstallState = "installed" | "prompt" | "ios" | "none";

let deferred: InstallPromptEvent | null = null;
let installed = false;
let listening = false;
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((listener) => listener());

/** Whether the app runs installed, from the home screen or as an app window. */
export const standalone = (): boolean =>
  (navigator as Navigator & { standalone?: boolean }).standalone === true ||
  (typeof window.matchMedia === "function" &&
    window.matchMedia("(display-mode: standalone)").matches);

// iPadOS reports itself as a Mac; a Mac has no touch screen.
const ios = (): boolean =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export const installState = (): InstallState => {
  if (installed || standalone()) return "installed";
  if (deferred) return "prompt";
  return ios() ? "ios" : "none";
};

export const subscribeInstall = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Catch the browser's install events. Call once, at startup. */
export const listenForInstall = (): void => {
  if (listening) return;
  listening = true;
  window.addEventListener("beforeinstallprompt", (event) => {
    // Keep the browser's own mini-infobar away; our button offers it.
    event.preventDefault();
    deferred = event as InstallPromptEvent;
    changed();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    installed = true;
    changed();
  });
};

/** Open the browser's install dialog. An event can prompt only once. */
export const promptInstall = async (): Promise<boolean> => {
  const event = deferred;
  if (!event) return false;
  deferred = null;
  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    installed = outcome === "accepted";
    return installed;
  } finally {
    changed();
  }
};

// Home's suggestion, once put aside, stays away this long.
const DISMISS_KEY = "khipur-install-dismissed";
const DISMISS_DAYS = 30;

export const installDismissed = (now = Date.now()): boolean => {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY));
    return at > 0 && now - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

export const dismissInstall = (now = Date.now()): void => {
  try {
    localStorage.setItem(DISMISS_KEY, String(now));
  } catch {
    // Private mode or storage full: it just comes back next visit.
  }
  changed();
};

/** Forget the install event and state between tests. */
export const resetInstall = (): void => {
  deferred = null;
  installed = false;
  changed();
};
